import { useEffect, useRef } from "react"
import { t } from "@lingui/core/macro"
import { Trans } from "@lingui/react/macro"
import { createPortal } from "react-dom"
import { Sizes, Weights } from "../../../interfaces/sizing"
import { colorClass } from "../../../utils/css"
import { Button, ButtonSize, Overlay, Typography } from "../../common"
import "./AppDirConfirmationModal.scss"

interface AppDirConfirmationModalProps {
    isOpen: boolean
    onClose: () => void
    onSave: () => void
}

export const AppDirConfirmationModal = ({
    isOpen,
    onClose,
    onSave,
}: AppDirConfirmationModalProps) => {
    const cancelBtnRef = useRef<HTMLButtonElement>(null)
    const updateBtnRef = useRef<HTMLButtonElement>(null)

    useEffect(() => {
        const tabNav = (e: KeyboardEvent) => {
            if (e.key === "Tab") {
                e.preventDefault()
                e.stopPropagation()
                if (document.activeElement === updateBtnRef.current) {
                    cancelBtnRef.current?.focus()
                } else {
                    updateBtnRef.current?.focus()
                }
            }
            if (e.key === "Escape") {
                onClose()
            }
        }

        document.addEventListener("keydown", tabNav, { capture: true })
        return () => {
            document.removeEventListener("keydown", tabNav, { capture: true })
        }
    }, [])

    if (!isOpen) return null

    return createPortal(
        <>
            <Overlay
                className="AppDirConfirmationModal__overlay"
                dimmed={true}
                onClick={onClose}
            />
            <div
                className="AppDirConfirmationModal__container"
                role="dialog"
                aria-modal="true"
                autoFocus
                data-test="confirmation-modal"
            >
                <Typography
                    component="header"
                    size={Sizes.mdpx}
                    weight={Weights.bold}
                    color={colorClass.defaultTooltipColor}
                    className="AppDirConfirmationModal__header"
                >
                    <Trans>Are you sure?</Trans>
                </Typography>
                <Typography
                    component="p"
                    size={Sizes.smpx}
                    weight={Weights.normal}
                    color={colorClass.defaultTooltipColor}
                    className="AppDirConfirmationModal__content"
                    data-test="confirmation-message"
                >
                    <Trans>
                        The page will automatically refresh to update the app
                        directory design preferences. If you’re live, your
                        stream will be interrupted.
                    </Trans>
                </Typography>
                <Typography
                    component="p"
                    size={Sizes.smpx}
                    weight={Weights.normal}
                    color={colorClass.defaultTooltipColor}
                    className="AppDirConfirmationModal__additional-content"
                    data-test="additional-message"
                >
                    <Trans>
                        It is recommended to use the Modern Design, as it will
                        eventually replace the current Legacy Design.
                    </Trans>
                </Typography>
                <footer className="AppDirConfirmationModal__footer">
                    <Button
                        text={t`Cancel`}
                        size={ButtonSize.None}
                        onClick={onClose}
                        ref={cancelBtnRef}
                        data-test="cancel-btn"
                        className="AppDirConfirmationModal__footer-button AppDirConfirmationModal__footer-button--cancel"
                    />
                    <Button
                        text={t`Update`}
                        size={ButtonSize.None}
                        onClick={onSave}
                        ref={updateBtnRef}
                        autoFocus
                        data-test="update-btn"
                        className="AppDirConfirmationModal__footer-button AppDirConfirmationModal__footer-button--action"
                    />
                </footer>
            </div>
        </>,
        document.body,
    )
}
